'use strict';

/**
 * Important Project Files — host view (read-only navigator; management is entitlement-gated).
 *
 * Standalone project-scoped screen modeled on Sample Plan: a project-picker front door (shown when no project
 * is loaded) and, once a project is chosen (deep link #/ImportantFiles/:ID), a recursive collapsible folder
 * tree built from the provider's in-memory model. Client-side name filter + expand/collapse, plus per-file
 * download of the current version. With the 'manage-important-files' entitlement, per-row + toolbar affordances
 * appear (New folder / subfolder, rename, move, delete folder; rename, move, remove file; and Upload — toolbar
 * or per-folder, multi-file with a progress overlay) via modal prompts + confirms. A per-file Versions action
 * (read-only) lists every version with a download. Size visualization arrives in a later phase.
 */

const libPictView = require('pict-view');

const html = String.raw;
const fEsc = (pValue) => String(pValue == null ? '' : pValue).replace(/[&<>]/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[pChar]));
const fAttr = (pValue) => String(pValue == null ? '' : pValue).replace(/[&<>"']/g, (pChar) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[pChar]));

const FILE_ICONS = { File: 'fa-file-lines', Image: 'fa-file-image', Video: 'fa-file-video', Audio: 'fa-file-audio' };
const fFileIcon = (pType) => FILE_ICONS[pType] || 'fa-file-lines';

const fDate = (pValue) =>
{
	if (!pValue) { return ''; }
	const tmpDate = new Date(pValue);
	return Number.isNaN(tmpDate.getTime()) ? '' : tmpDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const TREE_MOUNT = '#ImportantFiles-Tree';

class ImportantFiles extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._projectFilterTimer = null;
		this._projectLoadToken = 0;
		this._collapsed = new Set();   // folder RowIDs the user has collapsed (open by default)
		this._nameFilter = '';
		this._nameFilterTimer = null;
		// When embedded in a host (the ProjectWorkspace Data ▸ Important Files tab) this holds that host selector,
		// and every render — including provider-triggered repaints — targets it instead of the app container.
		this._embedDestination = null;
	}

	get provider() { return this.pict.providers['ImportantFiles']; }
	get modal() { return this.pict.views['Pict-Section-Modal']; }
	_toast(pMessage, pType, pDuration) { if (this.modal && (typeof this.modal.toast === 'function')) { const tmpOptions = { type: pType || 'info' }; if (pDuration !== undefined) { tmpOptions.duration = pDuration; } this.modal.toast(pMessage, tmpOptions); } }

	/** Render into the embed host when embedded (so provider `_render()` repaints land in the Data tab, not the app
	 *  container). An explicit destination argument always wins. */
	render(pRenderableHash, pRenderDestinationAddress, pTemplateRecordAddress, pRootRenderable)
	{
		const tmpDestination = (typeof pRenderDestinationAddress === 'string') ? pRenderDestinationAddress : (this._embedDestination || undefined);
		return super.render(pRenderableHash, tmpDestination, pTemplateRecordAddress, pRootRenderable);
	}

	/** Embed the navigator into a host element for a fixed project (skips the front-door picker). ProjectWorkspace's
	 *  Data ▸ Important Files tab calls this after each paint; it only reloads when the loaded project differs. */
	openInto(pIDProject, pHostSelector)
	{
		this._embedDestination = pHostSelector;
		const tmpState = (this.pict.AppData && this.pict.AppData.ImportantFiles) || {};
		if (this.provider && (!tmpState.HasModel || (Number(this.provider.idProject) !== Number(pIDProject))))
		{
			this.provider.loadAsync(pIDProject);   // provider._render → this.render → the host
		}
		else { this.render(); }   // already loaded for this project — just (re)paint into the host
	}

	/** Leave embedded mode — the standalone #/ImportantFiles screen renders to the app container again. */
	exitEmbed() { this._embedDestination = null; }

	onBeforeRender(pRenderable)
	{
		const tmpState = (this.pict.AppData && this.pict.AppData.ImportantFiles) || {};
		// Mutually-exclusive body modes via the single-element-array slot trick (Sample Plan pattern).
		tmpState.LoadingSlot = tmpState.Loading ? [ tmpState ] : [];
		tmpState.ErrorSlot = (!tmpState.Loading && tmpState.Message) ? [ tmpState ] : [];
		tmpState.PickerSlot = (!tmpState.Loading && !tmpState.Message && !tmpState.HasModel) ? [ tmpState ] : [];
		tmpState.TreeSlot = (!tmpState.Loading && tmpState.HasModel) ? [ tmpState ] : [];
		// Management is entitlement-gated (Task-Catalog 'manage-important-files'); everyone else stays read-only.
		// The pill only shows once a project is loaded — no need on the picker.
		tmpState.CanManage = !!(this.provider && this.provider.canManage());
		tmpState.ReadOnlySlot = (tmpState.HasModel && !tmpState.Loading && !tmpState.CanManage) ? [ tmpState ] : [];
		tmpState.ManageSlot = (tmpState.HasModel && !tmpState.Loading && tmpState.CanManage) ? [ tmpState ] : [];
		tmpState.TitleText = (tmpState.HasModel && tmpState.ProjectName) ? `Important Files — ${tmpState.ProjectName}` : 'Important Project Files';
		tmpState.SubText = tmpState.HasModel
			? `${tmpState.FolderCount || 0} folder${(tmpState.FolderCount === 1) ? '' : 's'} · ${tmpState.FileCount || 0} file${(tmpState.FileCount === 1) ? '' : 's'}`
			: 'Organize a project’s important files into folders.';
		// Embedded in the ProjectWorkspace Data tab: drop the standalone header chrome + full-page framing (the
		// workspace supplies the project context); the standalone screen leaves this empty.
		tmpState.EmbedClass = this._embedDestination ? 'if-embedded' : '';
		this.pict.AppData.ImportantFiles = tmpState;
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable)
	{
		const tmpState = (this.pict.AppData && this.pict.AppData.ImportantFiles) || {};
		if (tmpState.HasModel && !tmpState.Loading) { this.renderTree(); }
		else if (tmpState.PickerSlot && tmpState.PickerSlot.length) { this._initProjectPicker(); }
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable);
	}

	// --- Front door: filterable project list (mirrors Sample Plan) ----------------------------------

	_initProjectPicker()
	{
		const tmpMount = this.pict.ContentAssignment.getElement('#ImportantFiles-ProjectList');
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		const tmpInput = document.getElementById('ImportantFiles-ProjectFilter');
		this._loadProjects(tmpInput ? tmpInput.value : '');
	}

	onProjectFilter(pValue)
	{
		if (this._projectFilterTimer) { clearTimeout(this._projectFilterTimer); }
		this._projectFilterTimer = setTimeout(() => { this._loadProjects(pValue); }, 200);
	}

	_loadProjects(pTerm)
	{
		const tmpProvider = this.provider;
		if (!tmpProvider) { return; }
		const tmpToken = (this._projectLoadToken = (this._projectLoadToken || 0) + 1);
		tmpProvider.searchProjectsAsync(pTerm).then((pProjects) =>
		{
			if (tmpToken === this._projectLoadToken) { this._renderProjectList(pProjects, pTerm); }
		}).catch(() =>
		{
			if (tmpToken === this._projectLoadToken) { this.pict.ContentAssignment.assignContent('#ImportantFiles-ProjectList', '<div class="if-note if-err">Could not load projects.</div>'); }
		});
	}

	_renderProjectList(pProjects, pTerm)
	{
		const tmpMount = this.pict.ContentAssignment.getElement('#ImportantFiles-ProjectList');
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		const tmpList = Array.isArray(pProjects) ? pProjects : [];
		if (!tmpList.length)
		{
			const tmpTerm = String(pTerm == null ? '' : pTerm).trim();
			this.pict.ContentAssignment.assignContent('#ImportantFiles-ProjectList', `<div class="if-note">${tmpTerm ? `No projects match “${fEsc(tmpTerm)}”.` : 'No projects available.'}</div>`);
			return;
		}
		const tmpRows = tmpList.map((pProject) =>
			`<button type="button" class="if-proj-row" onclick="_Pict.views.ImportantFiles.openProject(${Number(pProject.IDProject)})">`
			+ `<span class="if-proj-name">${fEsc(pProject.Name)}</span>`
			+ `<span class="if-proj-go"><i class="fas fa-chevron-right"></i></span></button>`).join('');
		const tmpCapNote = (tmpList.length >= 100) ? '<div class="if-note if-cap">Showing the first 100 — type to narrow the list.</div>' : '';
		this.pict.ContentAssignment.assignContent('#ImportantFiles-ProjectList', tmpRows + tmpCapNote);
	}

	/** Open a project's important files (deep link the router resolves + loads). */
	openProject(pIDProject)
	{
		const tmpID = Number(pIDProject);
		if (Number.isFinite(tmpID) && (tmpID > 0)) { window.location.hash = `#/ImportantFiles/${tmpID}`; }
	}

	/** Back to the project picker (the no-ID route resets the model). */
	chooseAnotherProject() { window.location.hash = '#/ImportantFiles'; }

	// --- Folder tree --------------------------------------------------------------------------------

	/** (Re)build the tree HTML into the mount, honoring the name filter + collapse state. */
	renderTree()
	{
		const tmpMount = this.pict.ContentAssignment.getElement(TREE_MOUNT);
		if (!tmpMount || (tmpMount.length === 0)) { return; }
		// Row-level management affordances render only when the entitlement is held (recomputed each render).
		this._canManage = !!(this.pict.AppData.ImportantFiles && this.pict.AppData.ImportantFiles.CanManage);
		const tmpTerm = String(this._nameFilter || '').trim().toLowerCase();
		const tmpTree = tmpTerm ? this._filterNodes(this.provider.tree, tmpTerm) : this.provider.tree;
		if (!tmpTree || !tmpTree.length)
		{
			const tmpEmpty = tmpTerm
				? `<div class="if-empty-state"><i class="fas fa-magnifying-glass"></i><p>No files or folders match “${fEsc(this._nameFilter)}”.</p></div>`
				: '<div class="if-empty-state"><i class="fas fa-folder-open"></i><p>This project has no important files yet.</p></div>';
			this.pict.ContentAssignment.assignContent(TREE_MOUNT, tmpEmpty);
			return;
		}
		// While filtering, force every surviving folder open so matches are visible.
		const tmpHTML = tmpTree.map((pNode) => this._nodeHTML(pNode, !!tmpTerm)).join('');
		this.pict.ContentAssignment.assignContent(TREE_MOUNT, tmpHTML);
	}

	/** Prune the tree to nodes matching the term: files by name; folders kept if their name matches (whole
	 *  subtree) or any descendant file matches. Returns a new array (does not mutate the model). */
	_filterNodes(pNodes, pTerm)
	{
		const tmpResult = [];
		for (let i = 0; i < pNodes.length; i++)
		{
			const tmpNode = pNodes[i];
			const tmpMatch = String(tmpNode.Name || '').toLowerCase().indexOf(pTerm) >= 0;
			if (!tmpNode.IsFolder)
			{
				if (tmpMatch) { tmpResult.push(tmpNode); }
				continue;
			}
			if (tmpMatch)
			{
				// Folder name matches -> keep it and its whole subtree.
				tmpResult.push(tmpNode);
				continue;
			}
			const tmpKeptChildren = this._filterNodes(tmpNode.Children || [], pTerm);
			if (tmpKeptChildren.length) { tmpResult.push(Object.assign({}, tmpNode, { Children: tmpKeptChildren })); }
		}
		return tmpResult;
	}

	_nodeHTML(pNode, pForceOpen)
	{
		const tmpManage = this._canManage;
		if (!pNode.IsFolder)
		{
			const tmpRowID = fAttr(pNode.RowID);
			const tmpActions = tmpManage
				? `<button type="button" class="if-act" title="Rename" aria-label="Rename ${fAttr(pNode.Name)}" onclick="_Pict.views.ImportantFiles.renameFile('${tmpRowID}', event)"><i class="fas fa-pen"></i></button>`
					+ `<button type="button" class="if-act" title="Move to another folder" onclick="_Pict.views.ImportantFiles.moveFile('${tmpRowID}', event)"><i class="fas fa-folder-tree"></i></button>`
					+ `<button type="button" class="if-act if-act-danger" title="Remove from important files" onclick="_Pict.views.ImportantFiles.removeFile('${tmpRowID}', event)"><i class="fas fa-xmark"></i></button>`
				: '';
			return html`<div class="if-file" data-file="${tmpRowID}" data-name="${fAttr(String(pNode.Name || '').toLowerCase())}">`
				+ `<i class="if-icon fas ${fFileIcon(pNode.FileType)}"></i>`
				+ `<button type="button" class="if-name if-file-name" title="Download current version" onclick="_Pict.views.ImportantFiles.downloadFile('${tmpRowID}', event)">${fEsc(pNode.Name)}</button>`
				+ `<span class="if-type">${fEsc(pNode.FileType)}</span>`
				+ `<span class="if-meta">${fEsc(fDate(pNode.Date))}</span>`
				+ `<span class="if-row-actions">${tmpActions}`
				+ `<button type="button" class="if-act" title="Versions" aria-label="Versions of ${fAttr(pNode.Name)}" onclick="_Pict.views.ImportantFiles.showVersions('${tmpRowID}', event)"><i class="fas fa-clock-rotate-left"></i></button>`
				+ `<button type="button" class="if-dl" title="Download current version" aria-label="Download ${fAttr(pNode.Name)}" onclick="_Pict.views.ImportantFiles.downloadFile('${tmpRowID}', event)"><i class="fas fa-download"></i></button>`
				+ `</span></div>`;
		}
		const tmpRowID = fAttr(pNode.RowID);
		const tmpOpen = pForceOpen || !this._collapsed.has(pNode.RowID);
		const tmpChildren = pNode.Children || [];
		const tmpBody = tmpChildren.length
			? tmpChildren.map((pChild) => this._nodeHTML(pChild, pForceOpen)).join('')
			: '<div class="if-folder-empty">This folder is empty.</div>';
		const tmpFolderIcon = pNode.IsCustomer ? 'fa-box-archive' : (tmpOpen ? 'fa-folder-open' : 'fa-folder');
		const tmpBadge = tmpChildren.length ? `<span class="if-badge">${tmpChildren.length}</span>` : '';
		// The synthetic 'Customer Files' bucket isn't a real folder — no folder actions on it.
		const tmpActions = (tmpManage && !pNode.IsCustomer)
			? `<span class="if-row-actions">`
				+ `<button type="button" class="if-act" title="Upload files here" onclick="_Pict.views.ImportantFiles.uploadToFolder('${tmpRowID}', event)"><i class="fas fa-cloud-arrow-up"></i></button>`
				+ `<button type="button" class="if-act" title="Add an existing observation here" onclick="_Pict.views.ImportantFiles.addFromObservationsFolder('${tmpRowID}', event)"><i class="fas fa-clipboard-list"></i></button>`
				+ `<button type="button" class="if-act" title="New subfolder" onclick="_Pict.views.ImportantFiles.newSubfolder('${tmpRowID}', event)"><i class="fas fa-folder-plus"></i></button>`
				+ `<button type="button" class="if-act" title="Rename" onclick="_Pict.views.ImportantFiles.renameFolder('${tmpRowID}', event)"><i class="fas fa-pen"></i></button>`
				+ `<button type="button" class="if-act" title="Move" onclick="_Pict.views.ImportantFiles.moveFolder('${tmpRowID}', event)"><i class="fas fa-arrow-right-arrow-left"></i></button>`
				+ `<button type="button" class="if-act if-act-danger" title="Delete folder" onclick="_Pict.views.ImportantFiles.deleteFolder('${tmpRowID}', event)"><i class="fas fa-trash"></i></button>`
				+ `</span>`
			: '';
		return html`<div class="if-folder${pNode.IsCustomer ? ' is-customer' : ''}" data-folder="${tmpRowID}">`
			+ `<div class="if-frow">`
			+ `<button type="button" class="if-frow-toggle" onclick="_Pict.views.ImportantFiles.toggleFolder('${tmpRowID}')">`
			+ `<i class="if-caret fas ${tmpOpen ? 'fa-caret-down' : 'fa-caret-right'}"></i>`
			+ `<i class="if-icon if-foldericon fas ${tmpFolderIcon}"></i>`
			+ `<span class="if-name">${fEsc(pNode.Name)}</span>`
			+ tmpBadge
			+ (pNode.IsCustomer ? '<span class="if-tag" title="Files that are important but not in a folder">not in a folder</span>' : '')
			+ `</button>`
			+ tmpActions
			+ `</div>`
			+ `<div class="if-fbody${tmpOpen ? '' : ' is-hidden'}">${tmpBody}</div>`
			+ `</div>`;
	}

	/** Expand/collapse one folder by DOM toggle (keeps scroll; no full re-render). */
	toggleFolder(pRowID)
	{
		const tmpFolder = document.querySelector(`[data-folder="${(window.CSS && CSS.escape) ? CSS.escape(pRowID) : pRowID}"]`);
		if (!tmpFolder) { return; }
		const tmpBody = tmpFolder.querySelector(':scope > .if-fbody');
		const tmpCaret = tmpFolder.querySelector(':scope > .if-frow > .if-frow-toggle > .if-caret');
		const tmpIcon = tmpFolder.querySelector(':scope > .if-frow > .if-frow-toggle > .if-foldericon');
		if (!tmpBody) { return; }
		tmpBody.classList.toggle('is-hidden');
		const tmpCollapsed = tmpBody.classList.contains('is-hidden');
		if (tmpCaret) { tmpCaret.classList.toggle('fa-caret-down', !tmpCollapsed); tmpCaret.classList.toggle('fa-caret-right', tmpCollapsed); }
		if (tmpIcon && !tmpFolder.classList.contains('is-customer')) { tmpIcon.classList.toggle('fa-folder-open', !tmpCollapsed); tmpIcon.classList.toggle('fa-folder', tmpCollapsed); }
		if (tmpCollapsed) { this._collapsed.add(pRowID); } else { this._collapsed.delete(pRowID); }
	}

	expandAll() { this._collapsed.clear(); this.renderTree(); }

	collapseAll()
	{
		const fCollect = (pNodes) => pNodes.forEach((pNode) => { if (pNode.IsFolder) { this._collapsed.add(pNode.RowID); fCollect(pNode.Children || []); } });
		fCollect(this.provider.tree || []);
		this.renderTree();
	}

	onNameFilter(pValue)
	{
		if (this._nameFilterTimer) { clearTimeout(this._nameFilterTimer); }
		this._nameFilterTimer = setTimeout(() => { this._nameFilter = pValue || ''; this.renderTree(); }, 180);
	}

	// --- Download (Phase 2) -------------------------------------------------------------------------

	/** Download a file's CURRENT version. Fast path: stream a same-origin media URL straight to disk via a plain
	 *  download anchor (no memory blob — safe for big videos). Fallback: fetch the bytes through the authenticated
	 *  rest client (legacy rows / cross-origin) and save the blob. Per-row spinner while in flight; toast on
	 *  failure. Read-only — no server mutation. */
	async downloadFile(pRowID, pEvent)
	{
		if (pEvent && (typeof pEvent.stopPropagation === 'function')) { pEvent.stopPropagation(); }
		const tmpNode = this.provider && this.provider.findNode(pRowID);
		if (!tmpNode || tmpNode.IsFolder) { return; }
		const tmpRow = document.querySelector(`[data-file="${(window.CSS && CSS.escape) ? CSS.escape(pRowID) : pRowID}"]`);
		const tmpButton = tmpRow ? tmpRow.querySelector('.if-dl') : null;
		const tmpIcon = tmpButton ? tmpButton.querySelector('i') : null;
		if (tmpButton && tmpButton.classList.contains('is-loading')) { return; }   // a download is already in flight
		if (tmpButton) { tmpButton.classList.add('is-loading'); tmpButton.disabled = true; }
		if (tmpIcon) { tmpIcon.className = 'fas fa-spinner fa-spin'; }
		try
		{
			const tmpDirect = this.provider.directDownloadURL(tmpNode);
			if (tmpDirect)
			{
				// Anchor click is fire-and-forget (the browser's own download UI shows progress); a brief spinner
				// just acknowledges the click landed.
				this._triggerAnchorDownload(tmpDirect.url, tmpDirect.filename);
				await new Promise((resolve) => setTimeout(resolve, 600));
			}
			else
			{
				const tmpResult = await this.provider.resolveDownloadAsync(tmpNode);
				this._triggerBrowserDownload(tmpResult.blob, tmpResult.filename);
			}
		}
		catch (pError)
		{
			this.pict.log.error(`ImportantFiles: download failed for ${pRowID}: ${pError && pError.message ? pError.message : pError}`, pError);
			this._toast(`Could not download “${tmpNode.Name}”.${pError && pError.message ? ' ' + pError.message : ''}`, 'error', 6000);
		}
		finally
		{
			if (tmpButton) { tmpButton.classList.remove('is-loading'); tmpButton.disabled = false; }
			if (tmpIcon) { tmpIcon.className = 'fas fa-download'; }
		}
	}

	/** Stream a (same-origin) URL to disk via a transient download anchor — the browser attaches the session cookie
	 *  and honors the download attribute; nothing is buffered in memory. */
	_triggerAnchorDownload(pURL, pFilename)
	{
		const tmpAnchor = document.createElement('a');
		tmpAnchor.href = pURL;
		tmpAnchor.download = pFilename || 'download';
		tmpAnchor.rel = 'noopener';
		document.body.appendChild(tmpAnchor);
		tmpAnchor.click();
		tmpAnchor.remove();
	}

	/** Save an in-memory Blob to the user's machine via a transient object-URL anchor (fallback path). */
	_triggerBrowserDownload(pBlob, pFilename)
	{
		const tmpURL = URL.createObjectURL(pBlob);
		this._triggerAnchorDownload(tmpURL, pFilename);
		setTimeout(() => { try { URL.revokeObjectURL(tmpURL); } catch (pErr) { /* object URL already gone */ } }, 10000);
	}
	// --- Management (Phase 4: folder + file operations) ---------------------------------------------

	/** Run a provider mutation with consistent UX: success/error toast (the provider silently reloads the tree). */
	async _runManageOp(pWorkFn, pSuccessMessage)
	{
		try
		{
			await pWorkFn();
			if (pSuccessMessage) { this._toast(pSuccessMessage, 'success'); }
		}
		catch (pError)
		{
			this.pict.log.error(`ImportantFiles: management op failed: ${pError && pError.message ? pError.message : pError}`, pError);
			this._toast((pError && pError.message) ? pError.message : 'That didn’t work — please try again.', 'error', 6000);
		}
	}

	/** Prompt for a single line of text (rename/new name). Resolves to the trimmed value, or null if cancelled/empty. */
	async _promptText(pTitle, pLabel, pCurrent, pConfirmLabel, pPlaceholder)
	{
		const tmpChoice = await this.modal.show(
			{
				title: pTitle,
				content: `<p class="if-modal-label">${fEsc(pLabel)}</p><input type="text" id="if-text-input" class="input" value="${fAttr(pCurrent || '')}" placeholder="${fAttr(pPlaceholder || '')}">`,
				buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: pConfirmLabel || 'Save', Style: 'primary' } ],
				onOpen: () => { const tmpEl = document.getElementById('if-text-input'); if (tmpEl) { tmpEl.focus(); tmpEl.select(); } },
			});
		if (tmpChoice !== 'ok') { return null; }
		const tmpValue = ((document.getElementById('if-text-input') || {}).value || '').trim();
		return tmpValue || null;
	}

	/** Prompt for a destination folder (a <select> of real folders + 'Top level'). Resolves to a folder ID (0 =
	 *  top level), or null if cancelled. `pExcludeFolderID` omits a folder + its subtree (folder moves only). */
	async _promptFolderTarget(pTitle, pExcludeFolderID)
	{
		const tmpChoices = this.provider.folderChoices(pExcludeFolderID);
		const tmpOptions = [ '<option value="0">— Top level (not in a folder) —</option>' ]
			.concat(tmpChoices.map((pC) => `<option value="${pC.ID}">${'&nbsp;&nbsp;&nbsp;'.repeat(pC.Depth)}${fEsc(pC.Name)}</option>`))
			.join('');
		const tmpChoice = await this.modal.show(
			{
				title: pTitle,
				content: `<p class="if-modal-label">Move to</p><div class="select is-fullwidth"><select id="if-move-target">${tmpOptions}</select></div>`,
				buttons: [ { Hash: 'cancel', Label: 'Cancel' }, { Hash: 'ok', Label: 'Move', Style: 'primary' } ],
			});
		if (tmpChoice !== 'ok') { return null; }
		const tmpValue = (document.getElementById('if-move-target') || {}).value;
		return (tmpValue == null) ? null : Number(tmpValue);
	}

	newRootFolder() { this._createFolderFlow(0, 'New folder'); }

	newSubfolder(pRowID, pEvent)
	{
		if (pEvent && pEvent.stopPropagation) { pEvent.stopPropagation(); }
		const tmpNode = this.provider.findNode(pRowID);
		if (!tmpNode) { return; }
		this._createFolderFlow(tmpNode.ID, `New folder in “${tmpNode.Name}”`);
	}

	async _createFolderFlow(pParentID, pTitle)
	{
		const tmpName = await this._promptText(pTitle, 'Folder name', '', 'Create folder', 'e.g. Inspection Photos');
		if (!tmpName) { return; }
		await this._runManageOp(() => this.provider.createFolderAsync(tmpName, pParentID), `Created “${tmpName}”.`);
	}

	async renameFolder(pRowID, pEvent)
	{
		if (pEvent && pEvent.stopPropagation) { pEvent.stopPropagation(); }
		const tmpNode = this.provider.findNode(pRowID);
		if (!tmpNode) { return; }
		const tmpName = await this._promptText('Rename folder', 'Folder name', tmpNode.Name, 'Save');
		if (!tmpName || tmpName === tmpNode.Name) { return; }
		await this._runManageOp(() => this.provider.renameFolderAsync(tmpNode, tmpName), `Renamed to “${tmpName}”.`);
	}

	async renameFile(pRowID, pEvent)
	{
		if (pEvent && pEvent.stopPropagation) { pEvent.stopPropagation(); }
		const tmpNode = this.provider.findNode(pRowID);
		if (!tmpNode) { return; }
		const tmpName = await this._promptText('Rename file', 'File name', tmpNode.Name, 'Save');
		if (!tmpName || tmpName === tmpNode.Name) { return; }
		await this._runManageOp(() => this.provider.renameFileAsync(tmpNode, tmpName), `Renamed to “${tmpName}”.`);
	}

	async moveFolder(pRowID, pEvent)
	{
		if (pEvent && pEvent.stopPropagation) { pEvent.stopPropagation(); }
		const tmpNode = this.provider.findNode(pRowID);
		if (!tmpNode) { return; }
		const tmpTarget = await this._promptFolderTarget(`Move “${tmpNode.Name}”`, tmpNode.ID);
		if (tmpTarget == null) { return; }
		await this._runManageOp(() => this.provider.moveFolderAsync(tmpNode, tmpTarget), `Moved “${tmpNode.Name}”.`);
	}

	async moveFile(pRowID, pEvent)
	{
		if (pEvent && pEvent.stopPropagation) { pEvent.stopPropagation(); }
		const tmpNode = this.provider.findNode(pRowID);
		if (!tmpNode) { return; }
		const tmpTarget = await this._promptFolderTarget(`Move “${tmpNode.Name}”`, null);
		if (tmpTarget == null) { return; }
		await this._runManageOp(() => this.provider.moveFileAsync(tmpNode, tmpTarget), `Moved “${tmpNode.Name}”.`);
	}

	async deleteFolder(pRowID, pEvent)
	{
		if (pEvent && pEvent.stopPropagation) { pEvent.stopPropagation(); }
		const tmpNode = this.provider.findNode(pRowID);
		if (!tmpNode) { return; }
		if (tmpNode.Children && tmpNode.Children.length)
		{
			this._toast('This folder isn’t empty. Move or remove everything inside it first.', 'warning', 5000);
			return;
		}
		const tmpOk = await this.modal.confirm(`Delete the folder “${tmpNode.Name}”?`, { title: 'Delete folder', confirmLabel: 'Delete', cancelLabel: 'Cancel', dangerous: true });
		if (!tmpOk) { return; }
		await this._runManageOp(() => this.provider.deleteFolderAsync(tmpNode), `Deleted “${tmpNode.Name}”.`);
	}

	async removeFile(pRowID, pEvent)
	{
		if (pEvent && pEvent.stopPropagation) { pEvent.stopPropagation(); }
		const tmpNode = this.provider.findNode(pRowID);
		if (!tmpNode) { return; }
		const tmpOk = await this.modal.confirm(`Remove “${tmpNode.Name}” from this project’s important files? The file stays on its observation — this only unlists it here.`, { title: 'Remove file', confirmLabel: 'Remove', cancelLabel: 'Cancel', dangerous: true });
		if (!tmpOk) { return; }
		await this._runManageOp(() => this.provider.removeFileAsync(tmpNode), `Removed “${tmpNode.Name}”.`);
	}

	// --- Upload (Phase 5) ---------------------------------------------------------------------------

	/** Toolbar upload → top level (not in a folder). */
	uploadToRoot() { this._pickAndUpload(0); }

	/** Folder-row 'upload here' → into that folder. */
	uploadToFolder(pRowID, pEvent)
	{
		if (pEvent && pEvent.stopPropagation) { pEvent.stopPropagation(); }
		const tmpNode = this.provider.findNode(pRowID);
		if (tmpNode) { this._pickAndUpload(tmpNode.ID); }
	}

	/** Open the OS file picker (multi-select) and upload whatever is chosen into pFolderID. */
	_pickAndUpload(pFolderID)
	{
		let tmpInput = document.getElementById('ImportantFiles-FileInput');
		if (!tmpInput)
		{
			tmpInput = document.createElement('input');
			tmpInput.type = 'file';
			tmpInput.id = 'ImportantFiles-FileInput';
			tmpInput.multiple = true;
			tmpInput.style.display = 'none';
			document.body.appendChild(tmpInput);
		}
		tmpInput.value = '';
		tmpInput.onchange = () =>
		{
			const tmpFiles = Array.from(tmpInput.files || []);
			tmpInput.onchange = null;
			if (tmpFiles.length) { this._uploadFiles(tmpFiles, pFolderID); }
		};
		tmpInput.click();
	}

	/** Upload a batch sequentially with a progress overlay; reload the tree once at the end; summarize via toast. */
	async _uploadFiles(pFiles, pFolderID)
	{
		const tmpTotal = pFiles.length;
		let tmpDone = 0;
		const tmpFailed = [];
		this._showUploadProgress();
		for (let i = 0; i < pFiles.length; i++)
		{
			const tmpFile = pFiles[i];
			const tmpLabel = `Uploading ${tmpFile.name} (${i + 1} of ${tmpTotal})…`;
			this._updateUploadProgress(null, tmpLabel);
			try
			{
				await this.provider.uploadFileAsync(tmpFile, pFolderID, (pEvent) =>
				{
					const tmpPct = this._progressPercent(pEvent);
					if (tmpPct !== null) { this._updateUploadProgress(tmpPct, tmpLabel); }
				});
				tmpDone++;
			}
			catch (pError)
			{
				this.pict.log.error(`ImportantFiles: upload failed for ${tmpFile.name}: ${pError && pError.message ? pError.message : pError}`, pError);
				tmpFailed.push(tmpFile.name);
			}
		}
		this._hideUploadProgress();
		try { await this.provider._reloadTreeAsync(); } catch (pErr) { /* the uploads landed; a failed refresh self-heals on next load */ }
		if (!tmpFailed.length) { this._toast(`Uploaded ${tmpDone} file${tmpDone === 1 ? '' : 's'}.`, 'success'); }
		else if (tmpDone > 0) { this._toast(`Uploaded ${tmpDone}, but ${tmpFailed.length} failed: ${tmpFailed.join(', ')}`, 'warning', 7000); }
		else { this._toast(`Upload failed: ${tmpFailed.join(', ')}`, 'error', 7000); }
	}

	/** Coax a 0–100 percentage out of whatever postBinary hands the progress callback (ProgressEvent, {percent},
	 *  or a bare number), or null when it isn't computable (→ the bar stays indeterminate). */
	_progressPercent(pEvent)
	{
		if (pEvent == null) { return null; }
		if (typeof pEvent === 'number') { return (pEvent <= 1) ? Math.round(pEvent * 100) : Math.round(pEvent); }
		if (pEvent.lengthComputable && pEvent.total) { return Math.round((pEvent.loaded / pEvent.total) * 100); }
		if (typeof pEvent.percent === 'number') { return Math.round(pEvent.percent); }
		if ((typeof pEvent.loaded === 'number') && (typeof pEvent.total === 'number') && pEvent.total) { return Math.round((pEvent.loaded / pEvent.total) * 100); }
		return null;
	}

	_showUploadProgress()
	{
		this._hideUploadProgress();
		const tmpEl = document.createElement('div');
		tmpEl.id = 'ImportantFiles-UploadProgress';
		tmpEl.className = 'if-upload-overlay';
		tmpEl.innerHTML = '<div class="if-upload-card"><div class="if-upload-title"><i class="fas fa-cloud-arrow-up"></i> Uploading</div>'
			+ '<div class="if-upload-label">Preparing…</div><div class="if-upload-bar"><div class="if-upload-fill is-indeterminate"></div></div></div>';
		document.body.appendChild(tmpEl);
	}

	_updateUploadProgress(pPct, pLabel)
	{
		const tmpEl = document.getElementById('ImportantFiles-UploadProgress');
		if (!tmpEl) { return; }
		const tmpFill = tmpEl.querySelector('.if-upload-fill');
		const tmpLabel = tmpEl.querySelector('.if-upload-label');
		if (tmpLabel && pLabel) { tmpLabel.textContent = pLabel; }
		if (tmpFill && (pPct !== null) && (pPct !== undefined))
		{
			tmpFill.classList.remove('is-indeterminate');
			tmpFill.style.width = Math.max(0, Math.min(100, pPct)) + '%';
		}
	}

	_hideUploadProgress() { const tmpEl = document.getElementById('ImportantFiles-UploadProgress'); if (tmpEl) { tmpEl.remove(); } }

	// --- Add existing observations (list an observation's file without re-uploading it) -------------

	/** Folder-row 'add observation here' → open the picker with that folder pre-selected as the destination. */
	addFromObservationsFolder(pRowID, pEvent)
	{
		if (pEvent && pEvent.stopPropagation) { pEvent.stopPropagation(); }
		const tmpNode = this.provider.findNode(pRowID);
		this.addFromObservations(tmpNode ? tmpNode.ID : 0);
	}

	/**
	 * Open a searchable picker of the project's file/photo observations; each row adds a ProjectFile pointer into a
	 * chosen destination folder (no re-upload — it lists the existing observation's file). Multi-add: the modal
	 * stays open so several can be added; the tree reloads once on close if anything was added.
	 * @param {Number} pFolderID destination folder to pre-select (0 = top level)
	 */
	async addFromObservations(pFolderID)
	{
		this._addObsFilterTimer = null;
		this._addObsLoadToken = 0;
		this._addObsAddedCount = 0;
		this._addObsRowsByID = {};
		const tmpFolderOptions = [ '<option value="0">— Top level (not in a folder) —</option>' ]
			.concat(this.provider.folderChoices(null).map((pC) => `<option value="${pC.ID}"${(Number(pC.ID) === Number(pFolderID)) ? ' selected' : ''}>${'&nbsp;&nbsp;&nbsp;'.repeat(pC.Depth)}${fEsc(pC.Name)}</option>`))
			.join('');
		const tmpContent = `<div class="if-addobs">`
			+ `<p class="if-modal-label">Add to folder</p>`
			+ `<div class="select is-fullwidth"><select id="if-addobs-folder">${tmpFolderOptions}</select></div>`
			+ `<div class="if-search if-addobs-search"><i class="fas fa-magnifying-glass"></i><input id="if-addobs-search" type="text" autocomplete="off" placeholder="Search files by name…" oninput="_Pict.views.ImportantFiles.onAddObsSearch(this.value)" /></div>`
			+ `<p class="if-addobs-note">Only observations that carry a file or photo can be added. Ones already listed here are hidden.</p>`
			+ `<div id="if-addobs-results" class="if-addobs-results"><div class="if-note"><i class="fas fa-spinner fa-spin"></i> Loading observations…</div></div>`
			+ `</div>`;
		await this.modal.show(
			{
				title: 'Add existing observation',
				content: tmpContent,
				buttons: [ { Hash: 'done', Label: 'Done', Style: 'primary' } ],
				width: '620px',
				onOpen: () => { const tmpEl = document.getElementById('if-addobs-search'); if (tmpEl) { tmpEl.focus(); } this._reloadAddObs(''); },
			});
		// Reflect all additions in the tree once, after the picker closes (per-add skips the reload).
		if (this._addObsAddedCount > 0) { try { await this.provider._reloadTreeAsync(); } catch (pErr) { /* the pointers landed; a failed refresh self-heals on next load */ } }
	}

	/** One-time load of the project's importable observations into the provider cache, then render (search over the
	 *  cache is client-side, so this only runs when the picker opens). */
	_reloadAddObs(pTerm)
	{
		const tmpMount = document.getElementById('if-addobs-results');
		if (!tmpMount) { return; }
		tmpMount.innerHTML = '<div class="if-note"><i class="fas fa-spinner fa-spin"></i> Loading observations…</div>';
		const tmpToken = (this._addObsLoadToken = (this._addObsLoadToken || 0) + 1);
		this.provider.loadImportableObservationsAsync().then(() =>
		{
			if (tmpToken === this._addObsLoadToken) { this._renderAddObsFromCache(pTerm); }
		}).catch(() =>
		{
			if (tmpToken === this._addObsLoadToken) { tmpMount.innerHTML = '<div class="if-note if-err">Could not load observations.</div>'; }
		});
	}

	onAddObsSearch(pValue)
	{
		if (this._addObsFilterTimer) { clearTimeout(this._addObsFilterTimer); }
		this._addObsFilterTimer = setTimeout(() => { this._renderAddObsFromCache(pValue); }, 160);
	}

	/** Render the cached importable observations narrowed by the current filename filter (client-side; sync). */
	_renderAddObsFromCache(pTerm)
	{
		const tmpMount = document.getElementById('if-addobs-results');
		if (!tmpMount) { return; }
		const tmpResult = this.provider.filterImportableObservations(pTerm);
		const tmpList = (tmpResult && tmpResult.rows) || [];
		this._addObsRowsByID = {};
		tmpList.forEach((pRow) => { this._addObsRowsByID[Number(pRow.IDObservation)] = pRow; });
		if (!tmpList.length)
		{
			const tmpTerm = String(pTerm == null ? '' : pTerm).trim();
			tmpMount.innerHTML = `<div class="if-note">${tmpTerm ? `No files match “${fEsc(tmpTerm)}”.` : 'No observations with a file to add.'}</div>`;
			return;
		}
		// Honest count line: how many matched, whether the render list was capped, and whether older files weren't fetched.
		let tmpNote = '';
		if (tmpResult.total > tmpResult.shown) { tmpNote = `Showing the first ${tmpResult.shown} of ${tmpResult.total} — type to narrow.`; }
		else if (tmpResult.truncated) { tmpNote = `Showing files from the ${this.provider.importableCap} most recent observations — type to narrow.`; }
		const tmpNoteHTML = tmpNote ? `<div class="if-note if-cap">${fEsc(tmpNote)}</div>` : '';
		tmpMount.innerHTML = tmpList.map((pRow) => this._addObsRowHTML(pRow)).join('') + tmpNoteHTML;
	}

	_addObsRowHTML(pRow)
	{
		const tmpID = Number(pRow.IDObservation);
		return `<div class="if-addobs-row" data-obs="${tmpID}">`
			+ `<i class="if-icon fas ${fFileIcon(pRow.FileType)}"></i>`
			+ `<span class="if-addobs-name" title="${fAttr(pRow.Name)}">${fEsc(pRow.Name)}</span>`
			+ `<span class="if-type">${fEsc(pRow.FileType)}</span>`
			+ `<span class="if-meta">${fEsc(fDate(pRow.Date))}</span>`
			+ `<button type="button" class="if-addobs-add" onclick="_Pict.views.ImportantFiles.addObservationRow(${tmpID}, this)"><i class="fas fa-plus"></i> Add</button>`
			+ `</div>`;
	}

	_addObsHintFor(pIDObservation) { return (this._addObsRowsByID && this._addObsRowsByID[Number(pIDObservation)]) || null; }

	/** Add one observation row into the currently-selected destination folder; marks the row done on success. */
	async addObservationRow(pIDObservation, pButtonEl)
	{
		const tmpFolderEl = document.getElementById('if-addobs-folder');
		const tmpFolderID = tmpFolderEl ? Number(tmpFolderEl.value) : 0;
		if (pButtonEl) { pButtonEl.disabled = true; pButtonEl.innerHTML = '<i class="fas fa-spinner fa-spin"></i>'; }
		try
		{
			const tmpResult = await this.provider.addExistingObservationAsync(pIDObservation, tmpFolderID, this._addObsHintFor(pIDObservation), true);
			this._addObsAddedCount = (this._addObsAddedCount || 0) + 1;
			if (pButtonEl) { pButtonEl.classList.add('is-added'); pButtonEl.innerHTML = '<i class="fas fa-check"></i> Added'; }
			this._toast(`Added “${tmpResult.Name}”.`, 'success');
		}
		catch (pError)
		{
			this.pict.log.error(`ImportantFiles: add observation ${pIDObservation} failed: ${pError && pError.message ? pError.message : pError}`, pError);
			if (pButtonEl) { pButtonEl.disabled = false; pButtonEl.innerHTML = '<i class="fas fa-plus"></i> Add'; }
			this._toast((pError && pError.message) ? pError.message : 'Could not add that observation.', 'error', 6000);
		}
	}

	// --- Versions (Phase 6) -------------------------------------------------------------------------

	/** Open a modal listing every version of a file (newest first), each downloadable. A read affordance — no
	 *  entitlement needed. Extension point: per-version metadata, previews, restore, diffing. */
	async showVersions(pRowID, pEvent)
	{
		if (pEvent && pEvent.stopPropagation) { pEvent.stopPropagation(); }
		const tmpNode = this.provider.findNode(pRowID);
		if (!tmpNode || tmpNode.IsFolder) { return; }
		let tmpData;
		try { tmpData = await this.provider.listVersionsAsync(tmpNode); }
		catch (pError) { this._toast(`Could not load versions.${pError && pError.message ? ' ' + pError.message : ''}`, 'error', 6000); return; }
		// Cache the resolved artifact ref so per-version downloads skip re-fetching the observation.
		this._versionsRef = { idArtifact: tmpData.idArtifact, currentVersion: tmpData.currentVersion, fileName: tmpData.fileName, mime: tmpData.mime };
		const tmpRows = tmpData.versions.map((pVersion) => this._versionRowHTML(pRowID, pVersion)).join('');
		const tmpNote = (tmpData.versions.length === 1)
			? 'This file has a single version.'
			: `${tmpData.versions.length} versions — the current file plus its earlier uploads.`;
		await this.modal.show(
			{
				title: `Versions — ${tmpNode.Name}`,
				content: `<div class="if-versions"><p class="if-ver-note">${fEsc(tmpNote)}</p><div class="if-ver-list">${tmpRows}</div></div>`,
				buttons: [ { Hash: 'close', Label: 'Close', Style: 'primary' } ],
				width: '540px',
			});
	}

	_versionRowHTML(pRowID, pVersion)
	{
		const tmpDate = pVersion.date ? fDate(pVersion.date) : '';
		const tmpMeta = (pVersion.isCurrent ? '<span class="if-ver-current">Current</span>' : 'Previous version')
			+ (tmpDate ? ` · ${fEsc(tmpDate)}` : '');
		return `<div class="if-ver-row${pVersion.isCurrent ? ' is-current' : ''}">`
			+ `<span class="if-ver-badge"><i class="fas fa-code-branch"></i> v${pVersion.version}</span>`
			+ `<span class="if-ver-meta">${tmpMeta}</span>`
			+ `<button type="button" class="if-ver-dl" onclick="_Pict.views.ImportantFiles.downloadFileVersion('${fAttr(pRowID)}', ${Number(pVersion.version)}, this)"><i class="fas fa-download"></i> Download</button>`
			+ `</div>`;
	}

	/** Download one specific version (called from a version row's button, which passes itself for the spinner). */
	async downloadFileVersion(pRowID, pVersion, pButtonEl)
	{
		const tmpNode = this.provider.findNode(pRowID);
		if (!tmpNode) { return; }
		const tmpIcon = pButtonEl ? pButtonEl.querySelector('i') : null;
		if (pButtonEl) { pButtonEl.disabled = true; }
		if (tmpIcon) { tmpIcon.className = 'fas fa-spinner fa-spin'; }
		try
		{
			const tmpResult = await this.provider.downloadVersionAsync(tmpNode, pVersion, this._versionsRef);
			this._triggerBrowserDownload(tmpResult.blob, tmpResult.filename);
		}
		catch (pError)
		{
			this.pict.log.error(`ImportantFiles: version ${pVersion} download failed: ${pError && pError.message ? pError.message : pError}`, pError);
			this._toast(`Could not download version ${pVersion}.${pError && pError.message ? ' ' + pError.message : ''}`, 'error', 6000);
		}
		finally
		{
			if (pButtonEl) { pButtonEl.disabled = false; }
			if (tmpIcon) { tmpIcon.className = 'fas fa-download'; }
		}
	}

}

module.exports = ImportantFiles;
module.exports.default_configuration = {
	ViewIdentifier: 'ImportantFiles',
	DefaultRenderable: 'ImportantFilesWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
		.if-wrap { max-width: 1000px; margin: 0 auto; padding: 1.5rem 1.25rem 4rem; }
		.if-head { display: flex; align-items: flex-start; gap: 0.85rem; margin-bottom: 1.25rem; }
		.if-head-ic { flex: none; width: 2.4rem; height: 2.4rem; border-radius: 10px; display: grid; place-items: center; background: var(--theme-color-background-selected, #e3edfb); color: var(--theme-color-brand-primary, #156dd1); font-size: 1.1rem; }
		.if-title { font-size: 1.5rem; font-weight: 700; margin: 0; line-height: 1.15; color: var(--theme-color-text-primary, #1f2733); }
		.if-sub { margin: 0.15rem 0 0; color: var(--theme-color-text-secondary, #5b6673); font-size: 0.92rem; }
		.if-head-spacer { flex: 1 1 auto; }
		.if-head-btn { flex: none; display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.82rem; cursor: pointer; padding: 0.4rem 0.75rem; border-radius: 7px; border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #45505f); }
		.if-head-btn:hover { background: var(--theme-color-background-secondary, #f0f2f6); color: var(--theme-color-text-primary, #1f2733); }
		.if-ro-pill { flex: none; align-self: center; display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.72rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.03em; padding: 0.25rem 0.55rem; border-radius: 999px; background: var(--theme-color-background-secondary, #f0f2f6); color: var(--theme-color-text-muted, #8a94a3); border: 1px solid var(--theme-color-border-light, #e8ebf0); cursor: default; }
		.if-ro-pill i { font-size: 0.68rem; }

		.if-state { padding: 2.5rem 1rem; text-align: center; color: var(--theme-color-text-secondary, #5b6673); }
		.if-state i { margin-right: 0.4rem; }

		/* Front door */
		.if-picker { max-width: 620px; margin: 0.5rem auto 0; }
		.if-picker-h { font-size: 1.05rem; font-weight: 600; margin: 0 0 0.2rem; }
		.if-picker-sub { color: var(--theme-color-text-secondary, #5b6673); font-size: 0.9rem; margin: 0 0 0.9rem; }
		.if-proj-filter { width: 100%; padding: 0.6rem 0.8rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 8px; font: inherit; font-size: 0.95rem; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); margin-bottom: 0.8rem; }
		.if-proj-filter:focus { outline: 2px solid var(--theme-color-brand-primary, #156dd1); outline-offset: -1px; }
		.if-proj-list { border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 10px; overflow: hidden; }
		.if-proj-row { display: flex; align-items: center; gap: 0.6rem; width: 100%; text-align: left; border: none; border-bottom: 1px solid var(--theme-color-border-light, #e8ebf0); background: var(--theme-color-background-panel, #fff); padding: 0.65rem 0.9rem; cursor: pointer; font: inherit; color: var(--theme-color-text-primary, #1f2733); }
		.if-proj-row:last-child { border-bottom: none; }
		.if-proj-row:hover { background: var(--theme-color-background-secondary, #f0f2f6); }
		.if-proj-name { flex: 1 1 auto; font-size: 0.92rem; }
		.if-proj-go { flex: none; color: var(--theme-color-text-muted, #8a94a3); font-size: 0.8rem; }
		.if-note { padding: 0.9rem; color: var(--theme-color-text-muted, #8a94a3); font-size: 0.86rem; text-align: center; }
		.if-note.if-err { color: var(--theme-color-status-error, #cf5b5b); }
		.if-note.if-cap { border-top: 1px solid var(--theme-color-border-light, #e8ebf0); }

		/* Tree toolbar */
		.if-toolbar { display: flex; align-items: center; gap: 0.6rem; flex-wrap: wrap; margin-bottom: 0.85rem; }
		.if-search { position: relative; flex: 1 1 240px; }
		.if-search i { position: absolute; left: 0.7rem; top: 50%; transform: translateY(-50%); color: var(--theme-color-text-muted, #8a94a3); font-size: 0.85rem; }
		.if-search input { width: 100%; padding: 0.5rem 0.7rem 0.5rem 2rem; border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 8px; font: inherit; font-size: 0.9rem; background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-primary, #1f2733); }
		.if-search input:focus { outline: 2px solid var(--theme-color-brand-primary, #156dd1); outline-offset: -1px; }
		.if-tool-btn { display: inline-flex; align-items: center; gap: 0.4rem; font-size: 0.8rem; cursor: pointer; padding: 0.45rem 0.7rem; border-radius: 7px; border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #45505f); }
		.if-tool-btn:hover { background: var(--theme-color-background-secondary, #f0f2f6); color: var(--theme-color-text-primary, #1f2733); }

		/* Tree */
		.if-tree { border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 12px; background: var(--theme-color-background-panel, #fff); padding: 0.5rem 0.4rem; min-height: 6rem; }
		.if-folder { }
		.if-fbody { padding-left: 1.35rem; border-left: 1px dotted var(--theme-color-border-default, #d7dce3); margin-left: 0.75rem; }
		.if-fbody.is-hidden { display: none; }
		.if-frow { display: flex; align-items: center; border-radius: 7px; }
		.if-frow-toggle { display: flex; align-items: center; gap: 0.5rem; flex: 1 1 auto; min-width: 0; text-align: left; border: none; background: transparent; cursor: pointer; font: inherit; padding: 0.4rem 0.55rem; color: var(--theme-color-text-primary, #1f2733); }
		.if-frow:hover { background: var(--theme-color-background-secondary, #f0f2f6); }
		.if-row-actions { display: inline-flex; align-items: center; gap: 0.05rem; flex: none; padding-right: 0.25rem; }
		.if-file .if-row-actions { margin-left: 0.25rem; }
		.if-act { border: none; background: transparent; cursor: pointer; color: var(--theme-color-text-muted, #8a94a3); padding: 0.2rem 0.42rem; border-radius: 6px; opacity: 0; transition: opacity 0.12s ease, color 0.12s ease, background 0.12s ease; font-size: 0.8rem; }
		.if-frow:hover .if-act, .if-file:hover .if-act { opacity: 0.7; }
		.if-act:hover { opacity: 1; color: var(--theme-color-brand-primary, #156dd1); background: var(--theme-color-background-selected, #e3edfb); }
		.if-act.if-act-danger:hover { color: var(--theme-color-status-error, #cf5b5b); background: rgba(207, 91, 91, 0.12); }
		.if-act:focus-visible { opacity: 1; outline: 2px solid var(--theme-color-brand-primary, #156dd1); outline-offset: -1px; }
		.if-tool-primary { border-color: var(--theme-color-brand-primary, #156dd1); color: var(--theme-color-brand-primary, #156dd1); }
		.if-tool-primary:hover { background: var(--theme-color-background-selected, #e3edfb); color: var(--theme-color-brand-primary, #156dd1); }
		.if-modal-label { margin: 0 0 0.4rem; font-size: 0.85rem; color: var(--theme-color-text-secondary, #5b6673); }
		/* Add-existing-observation picker (modal) */
		.if-addobs-search { margin: 0.7rem 0 0; flex: none; }
		.if-addobs-note { font-size: 0.78rem; color: var(--theme-color-text-muted, #8a94a3); margin: 0.5rem 0 0.4rem; }
		.if-addobs-results { display: flex; flex-direction: column; gap: 0.35rem; max-height: 46vh; overflow-y: auto; margin-top: 0.4rem; }
		.if-addobs-row { display: flex; align-items: center; gap: 0.6rem; padding: 0.45rem 0.6rem; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; }
		.if-addobs-row .if-icon { color: var(--theme-color-text-secondary, #5b6673); }
		.if-addobs-name { flex: 1 1 auto; min-width: 0; font-size: 0.88rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--theme-color-text-primary, #1f2733); }
		.if-addobs-add { flex: none; display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.8rem; cursor: pointer; padding: 0.32rem 0.7rem; border-radius: 7px; border: 1px solid var(--theme-color-brand-primary, #156dd1); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-brand-primary, #156dd1); }
		.if-addobs-add:hover { background: var(--theme-color-background-selected, #e3edfb); }
		.if-addobs-add[disabled] { cursor: default; }
		.if-addobs-add.is-added { border-color: var(--theme-color-border-light, #e8ebf0); color: var(--theme-color-status-success, #3a9e5c); background: var(--theme-color-background-panel, #fff); }
		.if-upload-overlay { position: fixed; inset: 0; background: rgba(20, 25, 35, 0.4); display: flex; align-items: center; justify-content: center; z-index: 10000; }
		.if-upload-card { background: var(--theme-color-background-panel, #fff); border: 1px solid var(--theme-color-border-default, #d7dce3); border-radius: 12px; padding: 1.4rem 1.6rem; min-width: 340px; max-width: 80vw; box-shadow: 0 16px 44px rgba(0, 0, 0, 0.28); }
		.if-upload-title { font-size: 1rem; font-weight: 600; color: var(--theme-color-text-primary, #1f2733); margin-bottom: 0.7rem; }
		.if-upload-title i { color: var(--theme-color-brand-primary, #156dd1); margin-right: 0.35rem; }
		.if-upload-label { font-size: 0.86rem; color: var(--theme-color-text-secondary, #5b6673); margin-bottom: 0.7rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.if-upload-bar { height: 8px; border-radius: 999px; background: var(--theme-color-background-secondary, #f0f2f6); overflow: hidden; position: relative; }
		.if-upload-fill { height: 100%; background: var(--theme-color-brand-primary, #156dd1); border-radius: 999px; width: 0; transition: width 0.15s ease; }
		.if-upload-fill.is-indeterminate { width: 35%; animation: if-upload-slide 1.1s ease-in-out infinite; }
		@keyframes if-upload-slide { 0% { transform: translateX(-120%); } 100% { transform: translateX(320%); } }
		.if-versions { }
		.if-ver-note { font-size: 0.85rem; color: var(--theme-color-text-secondary, #5b6673); margin: 0 0 0.85rem; }
		.if-ver-list { display: flex; flex-direction: column; gap: 0.4rem; max-height: 55vh; overflow-y: auto; }
		.if-ver-row { display: flex; align-items: center; gap: 0.75rem; padding: 0.55rem 0.75rem; border: 1px solid var(--theme-color-border-light, #e8ebf0); border-radius: 8px; }
		.if-ver-row.is-current { border-color: var(--theme-color-brand-primary, #156dd1); background: var(--theme-color-background-selected, #e3edfb); }
		.if-ver-badge { flex: none; font-weight: 600; font-size: 0.85rem; font-variant-numeric: tabular-nums; color: var(--theme-color-text-primary, #1f2733); }
		.if-ver-badge i { color: var(--theme-color-text-muted, #8a94a3); margin-right: 0.25rem; font-size: 0.72rem; }
		.if-ver-meta { flex: 1 1 auto; font-size: 0.8rem; color: var(--theme-color-text-secondary, #5b6673); }
		.if-ver-current { display: inline-block; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-brand-primary, #156dd1); }
		.if-ver-dl { flex: none; display: inline-flex; align-items: center; gap: 0.35rem; font-size: 0.8rem; cursor: pointer; padding: 0.35rem 0.7rem; border-radius: 7px; border: 1px solid var(--theme-color-border-default, #d7dce3); background: var(--theme-color-background-panel, #fff); color: var(--theme-color-text-secondary, #45505f); }
		.if-ver-dl:hover { background: var(--theme-color-background-secondary, #f0f2f6); color: var(--theme-color-brand-primary, #156dd1); }
		.if-ver-dl[disabled] { opacity: 0.6; cursor: default; }
		.if-caret { width: 0.7rem; color: var(--theme-color-text-muted, #8a94a3); font-size: 0.8rem; flex: none; }
		.if-icon { flex: none; font-size: 0.92rem; }
		.if-foldericon { color: var(--theme-color-brand-primary, #156dd1); }
		.if-folder.is-customer > .if-frow .if-foldericon { color: var(--theme-color-text-muted, #8a94a3); }
		.if-name { flex: 0 1 auto; font-size: 0.9rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.if-frow .if-name { font-weight: 500; }
		.if-badge { flex: none; font-size: 0.68rem; font-weight: 600; color: var(--theme-color-text-muted, #8a94a3); background: var(--theme-color-background-secondary, #f0f2f6); border-radius: 999px; padding: 0.05rem 0.45rem; }
		.if-tag { flex: none; font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-text-muted, #8a94a3); }
		.if-file { display: flex; align-items: center; gap: 0.5rem; padding: 0.38rem 0.55rem 0.38rem 1.25rem; border-radius: 7px; }
		.if-file:hover { background: var(--theme-color-background-secondary, #f0f2f6); }
		.if-file .if-icon { color: var(--theme-color-text-secondary, #5b6673); }
		.if-file .if-name { flex: 1 1 auto; }
		.if-file-name { text-align: left; border: none; background: transparent; cursor: pointer; font: inherit; font-size: 0.9rem; color: var(--theme-color-text-primary, #1f2733); padding: 0; }
		.if-file-name:hover { color: var(--theme-color-brand-primary, #156dd1); text-decoration: underline; }
		.if-file-name:focus-visible { outline: 2px solid var(--theme-color-brand-primary, #156dd1); outline-offset: 2px; border-radius: 3px; }
		.if-dl { flex: none; border: none; background: transparent; cursor: pointer; color: var(--theme-color-text-muted, #8a94a3); padding: 0.2rem 0.4rem; border-radius: 6px; opacity: 0.5; transition: opacity 0.12s ease, color 0.12s ease, background 0.12s ease; }
		.if-file:hover .if-dl { opacity: 1; }
		.if-dl:hover { color: var(--theme-color-brand-primary, #156dd1); background: var(--theme-color-background-selected, #e3edfb); }
		.if-dl:focus-visible { opacity: 1; outline: 2px solid var(--theme-color-brand-primary, #156dd1); outline-offset: -1px; }
		.if-dl.is-loading { opacity: 1; cursor: default; color: var(--theme-color-text-muted, #8a94a3); background: transparent; }
		.if-dl[disabled] { cursor: default; }
		.if-type { flex: none; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.03em; color: var(--theme-color-text-muted, #8a94a3); }
		.if-meta { flex: none; font-size: 0.76rem; color: var(--theme-color-text-muted, #8a94a3); font-variant-numeric: tabular-nums; }
		.if-folder-empty { padding: 0.35rem 0.55rem 0.35rem 1.25rem; color: var(--theme-color-text-muted, #8a94a3); font-size: 0.82rem; font-style: italic; }
		.if-empty-state { text-align: center; padding: 2.5rem 1rem; color: var(--theme-color-text-muted, #8a94a3); }
		.if-empty-state i { font-size: 1.6rem; display: block; margin-bottom: 0.5rem; opacity: 0.7; }
		.if-empty-state p { margin: 0; font-size: 0.9rem; }

		/* Embedded in the ProjectWorkspace Data tab: shed the standalone page framing + header chrome (the workspace
		   header already names the project; the tab label already says "Important Files"). */
		.section.if-embedded { padding: 0; }
		.if-embedded .if-wrap { max-width: none; margin: 0; padding: 0; }
		.if-embedded .if-head { margin: 0 0 0.5rem; }
		.if-embedded .if-head-ic, .if-embedded .if-head > div, .if-embedded .if-head-spacer, .if-embedded .if-head-btn { display: none; }
	`,

	Templates:
	[
		{
			Hash: 'ImportantFiles-Template',
			Template: html`
				<section class="section {~D:AppData.ImportantFiles.EmbedClass~}">
					<div class="if-wrap">
						<div class="if-head">
							<span class="if-head-ic"><i class="fas fa-folder-tree"></i></span>
							<div>
								<h1 class="if-title">{~D:AppData.ImportantFiles.TitleText~}</h1>
								<p class="if-sub">{~D:AppData.ImportantFiles.SubText~}</p>
							</div>
							{~TS:ImportantFiles-ReadOnly:AppData.ImportantFiles.ReadOnlySlot~}
							<span class="if-head-spacer"></span>
							{~TS:ImportantFiles-ChangeProject:AppData.ImportantFiles.TreeSlot~}
						</div>
						{~TS:ImportantFiles-Loading:AppData.ImportantFiles.LoadingSlot~}
						{~TS:ImportantFiles-Error:AppData.ImportantFiles.ErrorSlot~}
						{~TS:ImportantFiles-Picker:AppData.ImportantFiles.PickerSlot~}
						{~TS:ImportantFiles-Body:AppData.ImportantFiles.TreeSlot~}
					</div>
				</section>`,
		},
		{ Hash: 'ImportantFiles-ReadOnly', Template: html`<span class="if-ro-pill" title="You can browse and download these files. Creating folders, moving, and adding or removing files needs the “Manage important project files” entitlement."><i class="fas fa-lock"></i> Read-only</span>` },
		{ Hash: 'ImportantFiles-NewFolderBtn', Template: html`<button type="button" class="if-tool-btn if-tool-primary" onclick="_Pict.views.ImportantFiles.newRootFolder()"><i class="fas fa-folder-plus"></i> New folder</button>` },
		{ Hash: 'ImportantFiles-AddObsBtn', Template: html`<button type="button" class="if-tool-btn" onclick="_Pict.views.ImportantFiles.addFromObservations(0)"><i class="fas fa-clipboard-list"></i> Add observation</button>` },
		{ Hash: 'ImportantFiles-UploadBtn', Template: html`<button type="button" class="if-tool-btn if-tool-primary" onclick="_Pict.views.ImportantFiles.uploadToRoot()"><i class="fas fa-cloud-arrow-up"></i> Upload</button>` },
		{ Hash: 'ImportantFiles-ChangeProject', Template: html`<button type="button" class="if-head-btn" onclick="_Pict.views.ImportantFiles.chooseAnotherProject()"><i class="fas fa-arrow-left"></i> Choose another project</button>` },
		{ Hash: 'ImportantFiles-Loading', Template: html`<div class="if-state"><i class="fas fa-spinner fa-spin"></i> Loading files…</div>` },
		{ Hash: 'ImportantFiles-Error', Template: html`<div class="if-state"><i class="fas fa-triangle-exclamation"></i> {~D:Record.Message~}</div>` },
		{
			Hash: 'ImportantFiles-Picker',
			Template: html`
				<div class="if-picker">
					<h2 class="if-picker-h">Open a project’s important files</h2>
					<p class="if-picker-sub">Pick a project from the list below, or type to filter by name.</p>
					<input id="ImportantFiles-ProjectFilter" class="if-proj-filter" type="text" autocomplete="off" placeholder="Filter projects by name…" oninput="_Pict.views.ImportantFiles.onProjectFilter(this.value)" />
					<div id="ImportantFiles-ProjectList" class="if-proj-list"><div class="if-note"><i class="fas fa-spinner fa-spin"></i> Loading projects…</div></div>
				</div>`,
		},
		{
			Hash: 'ImportantFiles-Body',
			Template: html`
				<div class="if-toolbar">
					<div class="if-search"><i class="fas fa-magnifying-glass"></i><input type="text" autocomplete="off" placeholder="Filter files &amp; folders…" oninput="_Pict.views.ImportantFiles.onNameFilter(this.value)" /></div>
					<button type="button" class="if-tool-btn" onclick="_Pict.views.ImportantFiles.expandAll()"><i class="fas fa-angles-down"></i> Expand all</button>
					<button type="button" class="if-tool-btn" onclick="_Pict.views.ImportantFiles.collapseAll()"><i class="fas fa-angles-up"></i> Collapse all</button>
					{~TS:ImportantFiles-NewFolderBtn:AppData.ImportantFiles.ManageSlot~}
					{~TS:ImportantFiles-AddObsBtn:AppData.ImportantFiles.ManageSlot~}
					{~TS:ImportantFiles-UploadBtn:AppData.ImportantFiles.ManageSlot~}
				</div>
				<div id="ImportantFiles-Tree" class="if-tree"></div>`,
		},
	],

	Renderables:
	[
		{ RenderableHash: 'ImportantFilesWrap', TemplateHash: 'ImportantFiles-Template', DestinationAddress: '#PlatformConfiguration-Application-Container' },
	],
};
